import { ReadonlyVec3 } from 'gl-matrix';
import { sampleVolumeAtStride, trimmedRange } from './percentileWindow';
import { MipVolume } from './volumeMip';

/**
 * Pixel enlargement over the largest voxel step, so neighbouring voxels never
 * skip a pixel through rounding when their centres fall on pixel edges.
 */
const PIXEL_MARGIN = 1e-6;

/**
 * Column offset that keeps square-on voxel centres off pixel edges, where
 * {@link PIXEL_MARGIN} would merge two voxels. Marker and picking share it.
 */
const COLUMN_PHASE_PX = 0.25;

/** Footprint threshold as a fraction of the positive 99th percentile. */
export const FOOTPRINT_FRACTION_OF_HIGH_PERCENTILE = 0.01;

/** Keeps a whole-pixel extent from gaining an empty edge pixel by rounding. */
const EXTENT_EPSILON = 1e-9;

/**
 * The output frame of a turntable projection, the same at every azimuth.
 * Rows run superior (top) to inferior over every slice. The view turns about
 * the footprint's centre and the frame fits the footprint at any azimuth.
 */
export interface RotatedMipFrame {
    widthPx: number;
    heightPx: number;
    pixelWidthMm: number;
    pixelHeightMm: number;
    /** Voxel-space turning point (0 at the first voxel's corner). */
    centreVoxel: readonly [number, number, number];
    /** LPS mm displacement of one voxel step along each grid axis. */
    axisStepMm: readonly [ReadonlyVec3, ReadonlyVec3, ReadonlyVec3];
    sizeVoxels: readonly [number, number, number];
    /** Null when no voxel is positive. */
    footprint: RotatedMipFootprint | null;
}

export interface RotatedMipFootprint {
    /**
     * At or below is background. Rounded to float32 so it compares exactly
     * against projected pixel values.
     */
    threshold: number;
    /**
     * Per x line (`z * sizeY + y`), the half-open x range [first, end) of
     * voxels above {@link threshold}, at `2 * line`. Empty lines hold [0, 0].
     */
    spans: Int32Array;
}

/**
 * Which voxels a projection scatters: all, or only footprint spans. Pixels
 * above the threshold are identical either way.
 */
export type RotatedMipCoverage = 'volume' | 'footprint';

/**
 * A MIP from one azimuth. Every footprint voxel lands in exactly one pixel,
 * so none falls between rays. Values are uninterpolated voxel values.
 */
export interface RotatedMip {
    frame: RotatedMipFrame;
    coverage: RotatedMipCoverage;
    /** Degrees about the superior axis: 0 anterior, 90 left lateral. */
    azimuthDeg: number;
    /**
     * Per pixel, row-major. -Infinity where no finite voxel lands or, under
     * `footprint` coverage, only background does.
     */
    rayMaximum: Float32Array;
    /** Flat voxel index per pixel, or -1. Ties take the lowest index. */
    rayMaximumVoxelIndex: Int32Array;
}

export interface RotatedMipRayMaximum {
    voxelIndex: number;
    value: number;
}

/** LPS unit vector along the viewer's right: +X at 0, toward +Y as it grows. */
function viewerRight(azimuthDeg: number): [number, number] {
    const radians = (azimuthDeg * Math.PI) / 180;
    return [Math.cos(radians), Math.sin(radians)];
}

function axisSteps(
    volume: Pick<MipVolume, 'spacingMm' | 'orientation'>
): [ReadonlyVec3, ReadonlyVec3, ReadonlyVec3] {
    const { spacingMm, orientation } = volume;
    const stepAlong = (axis: 0 | 1 | 2): ReadonlyVec3 => [
        orientation[3 * axis] * spacingMm[axis],
        orientation[3 * axis + 1] * spacingMm[axis],
        orientation[3 * axis + 2] * spacingMm[axis],
    ];
    return [stepAlong(0), stepAlong(1), stepAlong(2)];
}

/**
 * The footprint threshold: 1% of the 99th percentile of the sampled positive
 * voxels, or null when none is positive. A percentile, so one hot voxel cannot
 * push the patient out of the frame; the hottest voxel is always above it.
 */
function footprintThreshold(voxels: ArrayLike<number>): number | null {
    let maximum = -Infinity;
    for (let i = 0; i < voxels.length; i++) {
        if (voxels[i] > maximum) {
            maximum = voxels[i];
        }
    }
    if (!(maximum > 0)) {
        return null;
    }
    const positive = sampleVolumeAtStride(voxels).filter((value) => value > 0);
    if (positive.length === 0) {
        return 0;
    }
    return trimmedRange(positive)[1] * FOOTPRINT_FRACTION_OF_HIGH_PERCENTILE;
}

/**
 * Spans, horizontal bounding-box centre (mm from the volume centre) and reach
 * of the footprint, or null. Position is affine along a line, so only span
 * ends are measured.
 */
function measureFootprint(
    volume: MipVolume,
    axisStepMm: readonly [ReadonlyVec3, ReadonlyVec3, ReadonlyVec3],
    threshold: number
): { spans: Int32Array; centre: [number, number]; reachMm: number } | null {
    const [sizeX, sizeY, sizeZ] = volume.sizeVoxels;
    const [stepX, stepY, stepZ] = axisStepMm;
    const voxels = volume.voxels;
    const originX =
        stepX[0] * (0.5 - sizeX / 2) +
        stepY[0] * (0.5 - sizeY / 2) +
        stepZ[0] * (0.5 - sizeZ / 2);
    const originY =
        stepX[1] * (0.5 - sizeX / 2) +
        stepY[1] * (0.5 - sizeY / 2) +
        stepZ[1] * (0.5 - sizeZ / 2);
    const lineCount = sizeY * sizeZ;

    const spans = new Int32Array(2 * lineCount);
    for (let line = 0; line < lineCount; line++) {
        const lineStart = line * sizeX;
        let first = 0;
        while (first < sizeX && !(voxels[lineStart + first] > threshold)) {
            first++;
        }
        if (first === sizeX) {
            continue;
        }
        let last = sizeX - 1;
        while (!(voxels[lineStart + last] > threshold)) {
            last--;
        }
        spans[2 * line] = first;
        spans[2 * line + 1] = last + 1;
    }

    const forEachSpanEnd = (
        visit: (positionX: number, positionY: number) => void
    ): void => {
        for (let line = 0; line < lineCount; line++) {
            const first = spans[2 * line];
            const end = spans[2 * line + 1];
            if (first >= end) {
                continue;
            }
            const y = line % sizeY;
            const z = (line - y) / sizeY;
            const lineX = originX + y * stepY[0] + z * stepZ[0];
            const lineY = originY + y * stepY[1] + z * stepZ[1];
            visit(lineX + first * stepX[0], lineY + first * stepX[1]);
            visit(lineX + (end - 1) * stepX[0], lineY + (end - 1) * stepX[1]);
        }
    };

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    forEachSpanEnd((positionX, positionY) => {
        minX = Math.min(minX, positionX);
        maxX = Math.max(maxX, positionX);
        minY = Math.min(minY, positionY);
        maxY = Math.max(maxY, positionY);
    });
    if (minX > maxX) {
        return null;
    }
    const centreX = (minX + maxX) / 2;
    const centreY = (minY + maxY) / 2;
    let reachSquared = 0;
    forEachSpanEnd((positionX, positionY) => {
        const dx = positionX - centreX;
        const dy = positionY - centreY;
        reachSquared = Math.max(reachSquared, dx * dx + dy * dy);
    });
    return {
        spans,
        centre: [centreX, centreY],
        reachMm: Math.sqrt(reachSquared),
    };
}

/**
 * The frame for every azimuth, cropped to the footprint, or whole when no
 * voxel is positive.
 * @throws on an empty volume or a voxel count that does not match the size.
 */
export function planRotatedMipFrame(volume: MipVolume): RotatedMipFrame {
    const { sizeVoxels } = volume;
    if (sizeVoxels.some((size) => !(size > 0))) {
        throw new Error(
            `planRotatedMipFrame: empty volume (${sizeVoxels.join('x')})`
        );
    }
    const voxelCount = sizeVoxels[0] * sizeVoxels[1] * sizeVoxels[2];
    if (volume.voxels.length !== voxelCount) {
        throw new Error(
            `planRotatedMipFrame: expected ${voxelCount} voxels, got ` +
                `${volume.voxels.length}`
        );
    }
    const axisStepMm = axisSteps(volume);

    // A step's projection onto the viewer's right never exceeds its
    // horizontal length, whatever the azimuth.
    let horizontalStep = 0;
    let verticalStep = 0;
    for (const step of axisStepMm) {
        horizontalStep = Math.max(horizontalStep, Math.hypot(step[0], step[1]));
        verticalStep = Math.max(verticalStep, Math.abs(step[2]));
    }
    const pixelWidthMm = horizontalStep * (1 + PIXEL_MARGIN);
    const pixelHeightMm = verticalStep * (1 + PIXEL_MARGIN);

    const volumeCentre: [number, number, number] = [
        sizeVoxels[0] / 2,
        sizeVoxels[1] / 2,
        sizeVoxels[2] / 2,
    ];
    let volumeRadiusMm = 0;
    let halfHeightMm = 0;
    for (let corner = 0; corner < 8; corner++) {
        const offset = [0, 0, 0];
        for (const axis of [0, 1, 2] as const) {
            const extent =
                (corner >> axis) & 1 ? volumeCentre[axis] : -volumeCentre[axis];
            for (let p = 0; p < 3; p++) {
                offset[p] += axisStepMm[axis][p] * extent;
            }
        }
        volumeRadiusMm = Math.max(
            volumeRadiusMm,
            Math.hypot(offset[0], offset[1])
        );
        halfHeightMm = Math.max(halfHeightMm, Math.abs(offset[2]));
    }

    // Rounded once to float32 so footprint, projection floor and picking
    // agree exactly at the boundary.
    const rawThreshold = footprintThreshold(volume.voxels);
    const threshold = rawThreshold === null ? null : Math.fround(rawThreshold);
    const footprint =
        threshold === null
            ? null
            : measureFootprint(volume, axisStepMm, threshold);

    // One pixel of margin keeps every footprint voxel centre strictly inside.
    const radiusMm = footprint
        ? footprint.reachMm + pixelWidthMm
        : volumeRadiusMm;
    const centreVoxel: [number, number, number] = [...volumeCentre];
    if (footprint) {
        const [offsetX, offsetY] = footprint.centre;
        for (const axis of [0, 1, 2] as const) {
            const step = axisStepMm[axis];
            // Offset along the axis in voxels: step·offset / spacing².
            centreVoxel[axis] +=
                (step[0] * offsetX + step[1] * offsetY) /
                (step[0] * step[0] + step[1] * step[1] + step[2] * step[2]);
        }
    }

    return {
        widthPx: Math.max(
            1,
            Math.ceil((2 * radiusMm) / pixelWidthMm - EXTENT_EPSILON)
        ),
        heightPx: Math.max(
            1,
            Math.ceil((2 * halfHeightMm) / pixelHeightMm - EXTENT_EPSILON)
        ),
        pixelWidthMm,
        pixelHeightMm,
        centreVoxel,
        axisStepMm,
        sizeVoxels: [sizeVoxels[0], sizeVoxels[1], sizeVoxels[2]],
        footprint:
            threshold !== null && footprint
                ? { threshold, spans: footprint.spans }
                : null,
    };
}

/** Voxel space to pixels: `px = origin + Σ perAxis[i] · voxel[i]`. */
interface PixelMap {
    columnOrigin: number;
    rowOrigin: number;
    columnPerAxis: [number, number, number];
    rowPerAxis: [number, number, number];
}

function pixelMap(frame: RotatedMipFrame, azimuthDeg: number): PixelMap {
    const [rightX, rightY] = viewerRight(azimuthDeg);
    const columnPerAxis = frame.axisStepMm.map(
        (step) => (step[0] * rightX + step[1] * rightY) / frame.pixelWidthMm
    ) as [number, number, number];
    // Superior (+Z) runs up the image, so rows decrease with it.
    const rowPerAxis = frame.axisStepMm.map(
        (step) => -step[2] / frame.pixelHeightMm
    ) as [number, number, number];
    let columnOrigin = frame.widthPx / 2 + COLUMN_PHASE_PX;
    let rowOrigin = frame.heightPx / 2;
    for (const axis of [0, 1, 2] as const) {
        columnOrigin -= columnPerAxis[axis] * frame.centreVoxel[axis];
        rowOrigin -= rowPerAxis[axis] * frame.centreVoxel[axis];
    }
    return { columnOrigin, rowOrigin, columnPerAxis, rowPerAxis };
}

/**
 * Scatters each voxel centre into its pixel, keeping the maximum. A frame
 * with no footprint is projected whole and reports `volume` coverage.
 * @throws if `voxels` does not match the frame's dimensions.
 */
export function computeRotatedMip(
    volume: Pick<MipVolume, 'voxels'>,
    frame: RotatedMipFrame,
    azimuthDeg: number,
    coverage: RotatedMipCoverage = 'volume'
): RotatedMip {
    const [sizeX, sizeY, sizeZ] = frame.sizeVoxels;
    const voxelCount = sizeX * sizeY * sizeZ;
    if (volume.voxels.length !== voxelCount) {
        throw new Error(
            `computeRotatedMip: expected ${voxelCount} voxels, got ` +
                `${volume.voxels.length}`
        );
    }
    const footprint = coverage === 'footprint' ? frame.footprint : null;
    const { widthPx, heightPx } = frame;
    // Starting at the threshold, a background voxel never wins a pixel.
    const rayMaximum = new Float32Array(widthPx * heightPx).fill(
        footprint ? footprint.threshold : -Infinity
    );
    const rayMaximumVoxelIndex = new Int32Array(widthPx * heightPx).fill(-1);

    const map = pixelMap(frame, azimuthDeg);
    const [columnPerX, columnPerY, columnPerZ] = map.columnPerAxis;
    const [rowPerX, rowPerY, rowPerZ] = map.rowPerAxis;
    const columnAtCentre =
        map.columnOrigin + 0.5 * (columnPerX + columnPerY + columnPerZ);
    const rowAtCentre = map.rowOrigin + 0.5 * (rowPerX + rowPerY + rowPerZ);
    const voxels = volume.voxels;

    for (let z = 0; z < sizeZ; z++) {
        for (let y = 0; y < sizeY; y++) {
            const line = z * sizeY + y;
            const first = footprint ? footprint.spans[2 * line] : 0;
            const end = footprint ? footprint.spans[2 * line + 1] : sizeX;
            const lineColumn = columnAtCentre + y * columnPerY + z * columnPerZ;
            const lineRow = rowAtCentre + y * rowPerY + z * rowPerZ;
            const lineStart = line * sizeX;
            for (let x = first; x < end; x++) {
                const value = voxels[lineStart + x];
                const column = lineColumn + x * columnPerX;
                const row = lineRow + x * rowPerX;
                // Only background can fall outside the frame.
                if (
                    column >= 0 &&
                    column < widthPx &&
                    row >= 0 &&
                    row < heightPx
                ) {
                    const pixel = (row | 0) * widthPx + (column | 0);
                    if (value > rayMaximum[pixel]) {
                        rayMaximum[pixel] = value;
                        rayMaximumVoxelIndex[pixel] = lineStart + x;
                    }
                }
            }
        }
    }
    if (footprint) {
        for (let pixel = 0; pixel < rayMaximum.length; pixel++) {
            if (rayMaximumVoxelIndex[pixel] < 0) {
                rayMaximum[pixel] = -Infinity;
            }
        }
    }

    return {
        frame,
        coverage: footprint ? 'footprint' : 'volume',
        azimuthDeg,
        rayMaximum,
        rayMaximumVoxelIndex,
    };
}

/**
 * Continuous [column, row] of a voxel-space position (0 at the first voxel's
 * corner), by the same map the projection scatters with.
 */
export function rotatedMipPointToPx(
    mip: RotatedMip,
    voxelPosition: ReadonlyVec3
): [number, number] {
    const map = pixelMap(mip.frame, mip.azimuthDeg);
    let column = map.columnOrigin;
    let row = map.rowOrigin;
    for (const axis of [0, 1, 2] as const) {
        column += map.columnPerAxis[axis] * voxelPosition[axis];
        row += map.rowPerAxis[axis] * voxelPosition[axis];
    }
    return [column, row];
}

/**
 * The voxel behind the pixel at [column, row], or null outside the frame or
 * on background: a background ray's maximum is arbitrary, not a pick.
 */
export function rotatedMipRayMaximumAt(
    mip: RotatedMip,
    positionPx: readonly [number, number]
): RotatedMipRayMaximum | null {
    const column = Math.floor(positionPx[0]);
    const row = Math.floor(positionPx[1]);
    const { widthPx, heightPx } = mip.frame;
    if (!(column >= 0 && column < widthPx && row >= 0 && row < heightPx)) {
        return null;
    }
    const pixel = row * widthPx + column;
    const voxelIndex = mip.rayMaximumVoxelIndex[pixel];
    const value = mip.rayMaximum[pixel];
    const footprint = mip.frame.footprint;
    if (
        voxelIndex < 0 ||
        footprint === null ||
        !(value > footprint.threshold)
    ) {
        return null;
    }
    return { voxelIndex, value };
}
